import React from "react";
import { Box, Button, styled, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import 'animate.css';
// Importar la imagen
import headerImg from "../assets/image__2_-removebg-preview.png";
// traducciones
import { useTranslation } from "react-i18next";
import AnimatedSection from "../components/AnimatedSection";

const Header = ({ title, subtitle, cta, ctaHref = "/contact", ctaExternal = false }) => {
  const { t } = useTranslation();
  const titleText = title ?? t("header.title");
  const subtitleText = subtitle ?? t("header.subtitle");
  const ctaText = cta ?? t("header.cta");
  const isHash = typeof ctaHref === "string" && ctaHref.startsWith("#");
  const asAnchor = ctaExternal || isHash;

  const CustomBox = styled(Box)(({ theme }) => ({

    display: "flex",
    justifyContent: "space-between", // Alineación horizontal
    alignItems: "center", // Centrar verticalmente
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(3), // Reducido padding-top
    backgroundColor: "transparent",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
    },
  }));

  const BoxText = styled(Box)(({ theme }) => ({
    flex: "1",
    paddingLeft: theme.spacing(8),
    [theme.breakpoints.down("md")]: {
      flex: "2",
      textAlign: "center",
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  }));

  return (
    <>
      <style>
        {`
          @keyframes logoFloat {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-8px);
            }
          }
          @keyframes gradientRotate {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }
          @keyframes colorShift {
            0% {
              filter: hue-rotate(0deg) blur(25px);
            }
            25% {
              filter: hue-rotate(90deg) blur(25px);
            }
            50% {
              filter: hue-rotate(180deg) blur(25px);
            }
            75% {
              filter: hue-rotate(270deg) blur(25px);
            }
            100% {
              filter: hue-rotate(360deg) blur(25px);
            }
          }
        `}
      </style>
      <CustomBox component="header">
        {/* Box de texto */}
        <AnimatedSection direction="left" delay={0.2}>
          <BoxText component="section">
            <Typography
              variant="h3"
              component="h1"
              sx={(theme) => ({
                fontWeight: 700,
                color: "black",
                fontSize: "2.5rem",
                [theme.breakpoints.down("md")]: {
                  fontSize: "1.8rem",
                },
              })}
            >
              {titleText}
            </Typography>

            <Typography
              variant="body1"
              component="p"
              sx={(theme) => ({
                py: 3,
                lineHeight: 1.6,
                color: "black",
                fontSize: "1rem", // Reducido de 1.5rem
                [theme.breakpoints.down("md")]: {
                  fontSize: "0.9rem", // Reducido de 1rem
                },
              })}
            >
              {subtitleText}
            </Typography>

            <Box>
              <Button
                component={asAnchor ? "a" : Link}
                href={asAnchor ? ctaHref : undefined}
                to={asAnchor ? undefined : ctaHref}
                target={ctaExternal && !isHash ? "_blank" : undefined}
                rel={ctaExternal && !isHash ? "noopener noreferrer" : undefined}
                variant="contained"
                className="glow-on-hover"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  textTransform: "none",
                  borderRadius: 10,
                  color: "#ffffff",
                  backgroundColor: "#000000",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  width: "auto",
                  height: "auto",
                  minHeight: "50px",
                  lineHeight: 1.2,
                  "&:hover": {
                    backgroundColor: "#333333",
                  },
                  "&.glow-on-hover": {
                    width: "auto",
                    height: "auto",
                  },
                }}
              >
                {ctaText}
              </Button>
            </Box>
          </BoxText>
        </AnimatedSection>

        {/* Box que contiene la imagen */}
        <AnimatedSection direction="right" delay={0.4}>
          <Box
            sx={(theme) => ({
              display: "flex",
              justifyContent: "center", // Centrar horizontalmente
              alignItems: "center", // Centrar verticalmente
              flex: "1", // Ocupar espacio adecuado
              height: "100%", // Tomar altura completa del contenedor
              [theme.breakpoints.down("md")]: {
                flex: "1",
                alignSelf: "center",
                justifyContent: "center", // Centra en pantallas pequeñas
              },
            })}
          >
            <Box
              sx={(theme) => ({
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                width: "75vh", // Grande en desktop como antes
                maxWidth: "100%",
                margin: "0 auto",
                [theme.breakpoints.down("md")]: {
                  width: "280px", // Pequeño en mobile
                  maxWidth: "70vw", // Se adapta al ancho de la pantalla
                },
              })}
            >
              <img
                src={headerImg}
                alt="headerImg"
                className="animate__animated animate__zoomIn animate__slow"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  width: "auto",
                  height: "auto",
                }}
              />
            </Box>
          </Box>
        </AnimatedSection>
      </CustomBox>
      {/* Flecha animada hacia abajo */}
      <Box
        className="animate__animated animate__bounce animate__infinite animate__slow"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 4,
          mb: 2,
          cursor: 'pointer',
          '&:hover': {
            transform: 'scale(1.1)',
            transition: 'transform 0.3s ease',
          },
        }}
        onClick={() => {
          window.scrollTo({
            top: window.innerHeight,
            behavior: 'smooth',
          });
        }}
      >
        <Typography
          variant="body2"
          className="animate__animated animate__pulse animate__infinite"
          sx={{
            color: 'black',
            fontSize: '0.85rem',
            mb: 1,
            fontWeight: 500,
            fontFamily: '"Montserrat", sans-serif',
          }}
        >
          {t('header.moreInfo')}
        </Typography>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: '2.5rem',
            color: 'black',
          }}
        />
      </Box>
    </>
  );
};

export default Header;
